import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Field, FieldLabel } from '@/components/shadcn/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select';

type Props<T extends FieldValues> = {
  control: Control<T>;
  name?: Path<T>;
  label?: string;
  priorities: Array<{ label: string; value: number | string }>;
};

export default function PriorityField<T extends FieldValues>({
  control,
  name,
  label,
  priorities,
}: Props<T>) {
  const { t } = useTranslation();

  return (
    <Controller
      control={control}
      name={name ?? ('priority' as Path<T>)}
      render={({ field }) => (
        <Field>
          <FieldLabel>{label ?? t('TASK_FIELD_PRIORITY')}</FieldLabel>
          <Select
            value={String(field.value)}
            onValueChange={(value) => {
              const priority = priorities.find(
                (priority) => String(priority.value) === value,
              );

              field.onChange(priority?.value);
            }}
          >
            <SelectTrigger
              className="w-full"
              data-testid="task-priority-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {priorities.map((priority) => (
                <SelectItem
                  key={priority.value}
                  value={priority.value.toString()}
                >
                  {priority.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}
    />
  );
}
